'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { ArrowLeftRight, ChevronDown, LogOut, User } from 'lucide-react';
import { signOut } from 'next-auth/react';
import { clsx } from 'clsx';
import {
  startAccountSwitch,
  useCurrentUser,
  useAdmin,
} from '@paeffgen/react/crm-documents/feature-auth';
import { getContextIdentity } from './context-identity';
import { CustomerContextPanel } from './customer-context-panel';

/**
 * Desktop-Pendant zur Mobile-Avatar-Pill: eine kompakte Kunden-Zeile, die ein
 * Popover mit dem geteilten `CustomerContextPanel` (Konto, Cross-Login,
 * Debitor-Suche, Ansprechpartner) öffnet. Hält die Sidebar-Spalte schlank, der
 * Kontext bleibt einen Klick entfernt. Abmelden sitzt — analog zum Mobile-
 * Sheet-Footer — als container-spezifische Aktion unten im Popover.
 *
 * Open/Close/Outside-Click/Escape spiegeln das bewährte `FavoriteLabelPopover`.
 */
export function CustomerContextPopover() {
  const { user } = useCurrentUser();
  const { isAdmin, allCustomers, selectedCustomers } = useAdmin();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const identity = getContextIdentity({ user, isAdmin, allCustomers, selectedCustomers });

  // Außerhalb-Klick schließt.
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Escape schließt.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  return (
    <div ref={containerRef} className="relative border-b border-zinc-200">
      {/* Trigger — öffnet das Kontext-Popover */}
      <div className="px-3 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={panelId}
          className="w-full min-w-0 flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-zinc-50 transition-colors text-left"
        >
          <span
            className={clsx(
              'w-9 h-9 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0',
              identity.avatarBg,
            )}
          >
            {identity.avatar.type === 'initials'
              ? identity.avatar.value
              : identity.avatar.type === 'count'
                ? identity.avatar.value
                : <User size={16} strokeWidth={2} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-bold text-zinc-900 text-sm truncate">
              {identity.primaryName}
            </span>
            <span className="flex items-center gap-1.5 mt-0.5">
              {isAdmin && (
                <span className="text-[9px] font-bold uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded shrink-0">
                  Admin
                </span>
              )}
              <span className="text-xs text-zinc-500 truncate">{identity.subline}</span>
            </span>
          </span>
          <ChevronDown
            size={16}
            className={clsx(
              'text-zinc-400 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </div>

      {/* Popover-Panel */}
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Kundenkontext"
          className="absolute left-3 top-full z-50 w-80 max-w-[calc(100vw-2rem)] max-h-[70dvh] overflow-y-auto bg-white rounded-xl shadow-xl border border-zinc-200 dark:border-white/10 p-4 animate-in fade-in zoom-in-95 duration-150"
        >
          <CustomerContextPanel variant="popover" />

          {/* Kontowechsel + Abmelden — container-spezifisch, unten im Popover */}
          <div className="mt-4 pt-3 border-t border-zinc-100 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => startAccountSwitch()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 py-2 text-sm font-semibold text-secondary hover:bg-zinc-50 transition-colors"
            >
              <ArrowLeftRight size={16} /> Mit anderem Konto anmelden
            </button>
            <button
              type="button"
              onClick={() => signOut({ callbackUrl: '/' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-red-100 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={16} /> Abmelden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
